import React from 'react';
import { PhoneIcon, CalendarIcon, XCircleIcon, ClockIcon, FileTextIcon, ZapIcon, BellRingIcon } from 'lucide-react';
import { AnyContact, Lead, Loan, Retention } from '../types';
interface ContactCardProps {
  contact: AnyContact;
  isActive: boolean;
  onClick: (contact: AnyContact) => void;
  onCall: (contact: AnyContact) => void;
  onSchedule?: (contact: AnyContact) => void;
  onRemove?: (contact: AnyContact) => void;
  removeLabel?: string;
}
export function ContactCard({
  contact,
  isActive,
  onClick,
  onCall,
  onSchedule,
  onRemove,
  removeLabel
}: ContactCardProps) {
  const today = new Date().toISOString().split('T')[0];
  const isFollowUpToday = contact.scheduledFollowUp === today;
  const borderStyle = isActive ? 'bg-olympian-gold/5 border-olympian-gold/30 shadow-[inset_4px_0_0_rgba(212,168,67,1)]' : isFollowUpToday ? 'bg-cyan-500/5 border-cyan-500/30 shadow-[inset_4px_0_0_rgba(6,182,212,0.8)] hover:bg-cyan-500/10' : 'bg-olympian-surface/50 border-olympian-border hover:bg-olympian-surface hover:border-olympian-textMuted/30';
  const renderLeadContent = (lead: Lead) => {
    const latestNote = lead.history.find(h => h.type === 'note') || lead.history[0];
    return <>
        <div className="col-span-4 flex items-center gap-3 min-w-0" data-id="element-41">
          <Avatar name={lead.name} data-id="element-42" />
          <div className="min-w-0" data-id="element-43">
            <div className="font-semibold text-theme text-sm truncate" data-id="element-44">
              {lead.name}
            </div>
            <div className="text-xs text-olympian-textMuted font-mono" data-id="element-45">
              {lead.phone}
            </div>
          </div>
        </div>
        <div className="col-span-2 flex flex-col gap-1" data-id="element-46">
          <div className="flex items-center gap-1.5 text-xs text-olympian-textMuted" data-id="element-47">
            <ZapIcon className="w-3 h-3 shrink-0" data-id="element-48" />
            <span className="truncate" data-id="element-49">{lead.source}</span>
          </div>
          <div className="flex items-center gap-1.5 text-xs text-olympian-textMuted" data-id="element-50">
            <ClockIcon className="w-3 h-3 shrink-0" data-id="element-51" />
            <span data-id="element-52">{lead.daysSinceContact}d ago</span>
          </div>
        </div>
        <div className="col-span-4 min-w-0" data-id="element-53">
          <div className="text-xs text-olympian-textMuted italic truncate leading-relaxed" title={latestNote?.description} data-id="element-54">
            &ldquo;{latestNote?.description}&rdquo;
          </div>
        </div>
      </>;
  };
  const renderLoanContent = (loan: Loan) => {
    const progress = loan.docsReceived / loan.docsTotal * 100;
    return <>
        <div className="col-span-4 flex items-center gap-3 min-w-0" data-id="element-55">
          <Avatar name={loan.name} data-id="element-56" />
          <div className="min-w-0" data-id="element-57">
            <div className="font-semibold text-theme text-sm truncate" data-id="element-58">
              {loan.name}
            </div>
            <div className="text-xs text-olympian-textMuted font-mono" data-id="element-59">
              {loan.phone}
            </div>
          </div>
        </div>
        <div className="col-span-2 flex flex-col gap-1" data-id="element-60">
          <div className="text-sm font-semibold text-emerald-400 font-mono" data-id="element-61">
            ${(loan.amount / 1000).toFixed(0)}k
          </div>
          <div className="text-xs text-olympian-textMuted truncate" data-id="element-62">
            {loan.loanType} • {loan.daysInPipeline}d
          </div>
        </div>
        <div className="col-span-4 pr-2" data-id="element-63">
          <div className="flex justify-between text-xs mb-1.5" data-id="element-64">
            <span className="text-olympian-textMuted flex items-center gap-1" data-id="element-65">
              <FileTextIcon className="w-3 h-3" data-id="element-66" /> Docs
            </span>
            <span className="font-medium text-theme font-mono" data-id="element-67">
              {loan.docsReceived}/{loan.docsTotal}
            </span>
          </div>
          <div className="w-full bg-olympian-navyLight rounded-full h-1.5 overflow-hidden" data-id="element-68">
            <div className="bg-emerald-500 h-1.5 rounded-full transition-all duration-500" style={{
            width: `${progress}%`
          }} data-id="element-69" />
          </div>
        </div>
      </>;
  };
  const renderRetentionContent = (retention: Retention) => {
    return <>
        <div className="col-span-4 flex items-center gap-3 min-w-0" data-id="element-70">
          <Avatar name={retention.name} data-id="element-71" />
          <div className="min-w-0" data-id="element-72">
            <div className="font-semibold text-theme text-sm truncate" data-id="element-73">
              {retention.name}
            </div>
            <div className="text-xs text-olympian-textMuted font-mono" data-id="element-74">
              {retention.phone}
            </div>
          </div>
        </div>
        <div className="col-span-2 flex flex-col gap-1" data-id="element-75">
          <div className="text-sm font-semibold text-olympian-gold font-mono" data-id="element-76">
            ${(retention.amount / 1000).toFixed(0)}k
          </div>
          <div className="flex items-center gap-1.5 text-xs text-olympian-textMuted" data-id="element-77">
            <CalendarIcon className="w-3 h-3 shrink-0" data-id="element-78" />
            <span data-id="element-79">
              {new Date(retention.originalLoanDate).toLocaleDateString()}
            </span>
          </div>
        </div>
        <div className="col-span-4 min-w-0" data-id="element-80">
          <div className="bg-olympian-navyLight text-olympian-textMuted text-xs px-2.5 py-1.5 rounded-md border border-olympian-border truncate" title={retention.triggerReason} data-id="element-81">
            <span className="text-theme font-medium" data-id="element-82">Trigger:</span>{' '}
            {retention.triggerReason}
          </div>
        </div>
      </>;
  };
  return <div className={`w-full text-left p-4 rounded-lg border transition-all duration-200 cursor-pointer group relative ${borderStyle}`} onClick={() => onClick(contact)} data-id="element-83">
      {/* Follow-up today indicator */}
      {isFollowUpToday && <div className="absolute top-2 right-3 flex items-center gap-1.5 bg-cyan-500/15 text-cyan-400 border border-cyan-500/30 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider group-hover:opacity-0 transition-opacity" data-id="element-84">
          <BellRingIcon className="w-3 h-3" data-id="element-85" />
          Follow-up today
        </div>}

      {/* Main content row */}
      <div className="flex items-center gap-2" data-id="element-86">
        <div className="flex-1 grid grid-cols-10 gap-4 items-center min-w-0" data-id="element-87">
          {contact.type === 'lead' && renderLeadContent(contact as Lead)}
          {contact.type === 'loan' && renderLoanContent(contact as Loan)}
          {contact.type === 'retention' && renderRetentionContent(contact as Retention)}
        </div>

        {/* Action buttons */}
        <div className="flex gap-1.5 shrink-0 opacity-0 group-hover:opacity-100 transition-opacity pl-2" onClick={e => e.stopPropagation()} data-id="element-88">
          <button onClick={() => onCall(contact)} className="bg-olympian-gold/10 text-olympian-gold hover:bg-olympian-gold hover:text-olympian-navy p-2 rounded-md transition-colors" title="Call" aria-label={`Call ${contact.name}`} data-id="element-89">
            <PhoneIcon className="w-3.5 h-3.5" data-id="element-90" />
          </button>
          {onSchedule && <button onClick={() => onSchedule(contact)} className="bg-olympian-surface text-olympian-textMuted hover:text-theme border border-olympian-border hover:border-olympian-textMuted p-2 rounded-md transition-colors" title="Schedule Follow-up" aria-label={`Schedule follow-up for ${contact.name}`} data-id="element-91">
              <CalendarIcon className="w-3.5 h-3.5" data-id="element-92" />
            </button>}
          {onRemove && <button onClick={() => onRemove(contact)} className="bg-olympian-surface text-olympian-textMuted hover:text-red-400 hover:bg-red-500/10 border border-olympian-border hover:border-red-500/30 p-2 rounded-md transition-colors" title={removeLabel || 'Remove'} aria-label={`${removeLabel || 'Remove'} ${contact.name}`} data-id="element-93">
              <XCircleIcon className="w-3.5 h-3.5" data-id="element-94" />
            </button>}
        </div>
      </div>
    </div>;
}
function Avatar({
  name
}: {
  name: string;
}) {
  return <div className="w-9 h-9 rounded-full bg-olympian-surface border border-olympian-border flex items-center justify-center text-olympian-gold font-bold text-sm shrink-0" data-id="element-95">
      {name.charAt(0)}
    </div>;
}